import { Close, Mail } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import React from "react";
import { useGetClientEmailTemplatesQuery } from "../../App/Queries/MessageQueries";
import { useMessageInputHandlers } from "../../App/State/Hooks/MessageDialog/useMessageInput";
import {
  useMessageTemplateHandlers,
  useMessageTemplateState,
} from "../../App/State/Hooks/MessageDialog/useMessageTemplate";
import { useAppSelector } from "../../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../../App/State/Slices/SelectedClientSlice";
import { IFeatureToggles } from "../../App/Typings/FeatureToggles";
import { ICandidateWithMessageInfo, IEmailTemplate, IMessageDetail } from "../../App/Typings/MessageText";
import { ITableColumn } from "../../App/Typings/TableColumn";
import ViewEmailModal from "../../MessagingCenter/EmailMarketing/Components/ViewEmailModal";
import { MessagesContextProvider } from "../../MessagingCenter/State/MessagesContext";
import MessageTemplateTableActions from "./Components/MessageTemplateTableActions";
import { MessageTemplateTable } from "./MessageTemplateTable";

const columnsNoActionButtons: ITableColumn[] = [
  { label: "Created", propName: "dateDisplay", order: 0 },
  { label: "Name", propName: "name", order: 1 },
  { label: "Subject", propName: "subject", order: 2 },
  { label: "Body", propName: "body", order: 3 },
  { label: "Visibility", propName: "visibility", order: 4 },
  { label: "Actions", propName: "actions", order: 5 },
];

export default function EmailTemplateListDialog({
  buttonName,
  handleSelected,
  handleShowEmailTemplateClick,
  handleCloseEmailTemplateDialog,
  showDialog,
  ButtonProps,
}: {
  buttonName: string;
  handleSelected?: (row: any, select: boolean, e: any) => void;
  handleShowEmailTemplateClick?: () => void;
  handleCloseEmailTemplateDialog?: () => void;
  showDialog?: boolean;
  ButtonProps?: ButtonProps;
}) {
  const { useCustomEmailDomain } = useFlags<IFeatureToggles>();
  const { handleApplyEmailTemplate } = useMessageInputHandlers();
  const { clientName, clientEmailAddress, senderEmailDomain } = useAppSelector(selectClientValue);
  const templateState = useMessageTemplateState();
  const { handleShowTemplateDialog, handleHideTemplateDialog, handleTemplateSelected } =
    useMessageTemplateHandlers();
  const { data: emailTemplates, isLoading } = useGetClientEmailTemplatesQuery();

  const [showPreview, setShowPreview] = React.useState(false);
  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = React.useState<IEmailTemplate | null>(null);
  const [previewTemplateView, setPreviewTemplateView] = React.useState<IMessageDetail | null>(null);
  const previewCandidate: Partial<ICandidateWithMessageInfo> = {
    firstName: "Preview",
    lastName: "Candidate",
  };

  const truncatedTableData =
    emailTemplates?.map((template): IEmailTemplate => {
      return {
        ...template,
        body: truncateMessageBody(template.body),
      };
    }) || [];

  let fromAddress = clientEmailAddress;
  if (useCustomEmailDomain && senderEmailDomain) {
    fromAddress = `recruiter@${senderEmailDomain}`;
  }

  const handleSelectedRow = (row: IEmailTemplate, select: boolean, e: any) => {
    const originalRow = emailTemplates?.find((template) => template.emailTemplateId === row.emailTemplateId);
    if (originalRow) {
      handleSelected
        ? handleSelected(originalRow, select, e)
        : handleTemplateSelected(originalRow, handleApplyEmailTemplate);
    }
  };

  const handlePreview = (row: IEmailTemplate) => {
    const originalRow = emailTemplates?.find((template) => template.emailTemplateId === row.emailTemplateId);
    if (!originalRow) {
      setSelectedPreviewTemplate(null);
      setPreviewTemplateView(null);
      setShowPreview(false);
      return;
    }
    setPreviewTemplateView({
      id: originalRow.emailTemplateId,
      toAddress: `${previewCandidate.firstName?.toLowerCase()}@example.com`,
      toName: previewCandidate.firstName + " " + previewCandidate.lastName,
      fromAddress,
      fromName: clientName,
      message: originalRow.body,
      subject: originalRow.subject,
      messageType: "DirectEmailMessage",
      replyToMessageId: "",
      userHasReplyNotification: false,
    });
    setSelectedPreviewTemplate(originalRow);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setTimeout(() => {
      setPreviewTemplateView(null);
    }, 300);
  };

  return (
    <Box>
      <Button
        startIcon={<Mail />}
        id="load-email-templates"
        disabled={isLoading || !emailTemplates || emailTemplates.length === 0}
        {...ButtonProps}
        aria-haspopup="true"
        disableElevation
        onClick={handleShowEmailTemplateClick || handleShowTemplateDialog}
      >
        {buttonName}
      </Button>
      <Dialog
        id="email-templates-dialog"
        aria-labelledby="email-templates-dialog"
        open={showDialog || templateState.showTemplateDialog}
        onClose={handleCloseEmailTemplateDialog || handleHideTemplateDialog}
        scroll="paper"
        fullWidth={true}
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxHeight: { xs: "calc(100vh - 1rem)", sm: "calc(100vh - 64px)" },
            height: { xs: "100vh", sm: "90vh" },
            width: { xs: "calc(100% - 1rem)", sm: "calc(100% - 2rem)" },
            m: { xs: "1rem", sm: "2rem" },
          },
        }}
      >
        <DialogTitle
          id="email-template-dialog-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            color: "white",
            backgroundColor: "#00af4a",
          }}
        >
          <Box>Email Templates</Box>
          <Box>
            <IconButton
              onClick={handleCloseEmailTemplateDialog || handleHideTemplateDialog}
              aria-label="Close Email Templates Window"
              aria-controls="email-template-dialog"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers={true}
          sx={{
            display: "grid",
            gridTemplateColumns: "100%",
            gridTemplateRows: "auto 1fr",
            height: "100%",
          }}
        >
          <MessageTemplateTable
            name="email"
            columns={columnsNoActionButtons}
            loading={isLoading}
            tableData={truncatedTableData.map((c: IEmailTemplate) => {
              return {
                ...c,
                actions: (
                  <MessageTemplateTableActions
                    id={c.emailTemplateId}
                    onPreviewClick={() => handlePreview(c)}
                  />
                ),
              };
            })}
            handleSelected={handleSelectedRow}
          />
          <MessagesContextProvider
            previewCandidate={previewCandidate}
            previewMessageDetail={previewTemplateView ?? undefined}
          >
            <ViewEmailModal
              showPreview={showPreview}
              onClose={handleClosePreview}
              size="md"
              title={`Preview Email Template: ${selectedPreviewTemplate?.name || ""}`}
            />
          </MessagesContextProvider>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <ButtonGroup>
            <Button
              style={{ marginTop: "20px" }}
              variant={"outlined"}
              color="primary"
              onClick={handleCloseEmailTemplateDialog || handleHideTemplateDialog}
            >
              Close
            </Button>
          </ButtonGroup>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export const MemoEmailTemplateListDialog = React.memo(EmailTemplateListDialog);

export function truncateMessageBody(body: string) {
  if (body.length > 100) {
    return body.substring(0, 100) + "...";
  }
  return body;
}
